import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UiToolsService} from '../../core/ui-tools.service';
import {AnnotationHandler} from '../../image-annotation/annotation-handler';
import {AnnotationService} from '../../image-annotation/annotation.service';
import {ImageItem} from '../../core/definitions/image-item';
import {ObjectMediaContainer, ObjectMediaType} from '../../core/definitions/object-media-container';
import {SectionsContainer} from '../../core/definitions/sections-container';
import {MediaTypeChanges} from '../media-type-changes';
import {Media} from "../../core/definitions/media";
import {MediaSegment} from "../../core/definitions/media-segment";
import {ActiveMediaComponent} from "../../shared/active-media/active-media.component";
import {MediaConversionWatcher} from "../../shared/media-conversion-watcher.service";
import {selectedMediaPosition} from "./media-position";

@Component({
  selector: 'app-media-carousel',
  templateUrl: './media-carousel.component.html',
  styleUrls: ['./media-carousel.component.scss'],
  standalone: false,
  providers: [MediaConversionWatcher]
})
export class MediaCarouselComponent implements AfterViewInit, OnChanges, OnDestroy {
  // Injections
  private readonly annotationService: AnnotationService = inject(AnnotationService);
  private readonly conversionWatcher: MediaConversionWatcher = inject(MediaConversionWatcher);
  private readonly elRef: ElementRef = inject(ElementRef);
  private readonly uiTools: UiToolsService = inject(UiToolsService);

  private static readonly ACTIVE_MEDIA_CONTAINER_SIZES: Readonly<Record<string, string>> = {
    Audio: 'audio',
    Video: 'video'
  };

  activeMediaContainerSize: string = 'default';
  zoomValue = 1;
  container: any;
  collapseZoomInButton = false;
  showAnnotationToggle = false;
  toggleAnnotations = false;
  resizeTimeOut: any;
  imageGallery = false;
  showMediaOrder = false;
  stopSizeWatch: string;
  mediumScreen: boolean;
  setSmallThumbSize = false;
  thumbSize = {
    small: 'TRANS__MEDIA_CAROUSEL__SIZE_BUTTON_SMALL',
    medium: 'TRANS__MEDIA_CAROUSEL__SIZE_BUTTON_MEDIUM',
    large: 'TRANS__MEDIA_CAROUSEL__SIZE_BUTTON_LARGE'
  };
  mediumScreenThumbnailWidth = 76; // 72px compact tile plus its 4px gap
  thumbWidthKeys = Object.keys(this.thumbSize);

  @ViewChild('container') containerElement: ElementRef;
  @ViewChild('mediaElementContainer') mediaElementContainer: ElementRef;
  @ViewChild(ActiveMediaComponent) private activeMediaComponent?: ActiveMediaComponent;
  @Input() mediaContainer: ObjectMediaContainer;
  @Input() startMediaId: string;
  @Input() sectionsContainer: SectionsContainer;
  @Input() curAnn: AnnotationHandler;
  @Input() isEditing: boolean;
  @Output() mediaUpdated = new EventEmitter<string>();
  @Output() openImageFullScreen = new EventEmitter<object>();
  @Output() openModelFullScreen = new EventEmitter<object>();
  @Output() objectRefresh = new EventEmitter<object>();

  @HostListener('window:resize')
  onResize() {
    // wait for resize to be finished before doing stuff
    if (this.resizeTimeOut) {
      clearTimeout(this.resizeTimeOut);
    }
    this.resizeTimeOut = setTimeout((() => {
      this.resize();
    }), 500);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.sectionsContainer?.rootObject) {
      this.showAnnotationToggle = this.sectionsContainer.rootObject.meta_type !== 'media';
      this.imageGallery = this.sectionsContainer.rootObject.meta_type === 'media';
    }
    this.mediumScreen = this.uiTools.setMediumScreenSizeOnLoad(this.mediumScreen);
    this.setThumbWidthFromWindowSize();
    this.setSmallThumbSize = window.innerHeight < 900 && !this.mediumScreen;
    if (changes.hasOwnProperty('mediaContainer') || changes.hasOwnProperty('startMediaId')) {
      this.setCurrentMediaFromStartMediaId();
    }
    if (changes.hasOwnProperty('mediaContainer')) {
      this.watchAudioConversions();
    }
    this.stopSizeWatch = this.uiTools.addWindowSizeListener(
      (newVal: any) => {
        this.mediumScreen = newVal.width < 1025;
      }
    );
  }

  ngAfterViewInit() {
    this.container = this.containerElement.nativeElement;
  }

  ngOnDestroy() {
    if (this.stopSizeWatch) {
      this.uiTools.removeWindowSizeListener(this.stopSizeWatch);
    }
  }

  get activeMedia() {
    let res = null;
    const mediaElement = this.mediaContainer.currentMediaType.mediaElements[
      this.mediaContainer.currentMediaType.currentMediaIndex];
    if (mediaElement) {
      res = mediaElement.mediaObject
    }
    return res;
  }

  private setCurrentMediaFromStartMediaId() {
    if (this.isCurrentMedia(this.startMediaId)) {
      return;
    }
    let indexSet = false;
    const mustSetIndex = this.startMediaId !== null;
    if (mustSetIndex) {
      for (const mediaType of this.mediaContainer.mediaTypes) {
        for (let i = 0; i < mediaType.mediaElements.length; i++) {
          if (mediaType.mediaElements[i].mediaObject.artifact_id === this.startMediaId) {
            indexSet = true;
            this.setCurrentMedia(mediaType, i).then();
            break;
          }
        }
        if (indexSet) {
          break;
        }
      }
    }
    if (!indexSet && this.mediaContainer.mediaTypes.length) {
      this.setCurrentMedia(this.mediaContainer.mediaTypes[0], 0).then();
    }
  }

  // Audio tiles show a placeholder image that never fails, so their conversion is looked up up front
  private watchAudioConversions() {
    const audio = (this.mediaContainer?.mediaTypes ?? [])
      .filter(mediaType => mediaType.mediaType === 'Audio')
      .flatMap(mediaType => mediaType.mediaElements.map(mediaElement => mediaElement.mediaObject));
    if (audio.length) {
      this.conversionWatcher.watchAll(audio);
    }
  }

  private isCurrentMedia(mediaId: string): boolean {
    const mediaType = this.mediaContainer?.currentMediaType;
    if (!mediaId || !mediaType) {
      return false;
    }
    return mediaType.mediaElements[mediaType.currentMediaIndex]?.mediaObject.artifact_id === mediaId;
  }

  private setMediaTypeCanAnnotate() {
    const mediaType = this.mediaContainer.currentMediaType;
    if (mediaType.mediaType === 'Image' && this.showAnnotationToggle && this.sectionsContainer?.rootObject) {
      this.annotationService.canAnnotate(this.sectionsContainer.rootObject).then(res => {
        mediaType.canAnnotate = res;
      });
    } else {
      mediaType.canAnnotate = false;
    }
  }

  async toggleAnnotation() {
    this.toggleAnnotations = !this.toggleAnnotations;
    this.updateActiveMediaContainerSize();
    this.collapseZoomInButton = true;
    this.onChangeZoomValue(1);
    if (this.toggleAnnotations) {
      this.setCurrentAnnotation().then();
    }
    setTimeout(() => this.collapseZoomInButton = false, 1000);
  }

  onSetActiveMedia(mediaType: ObjectMediaType, index: any, direction?: string) {
    const segmentOnly = this.getSegmentOnly(mediaType.mediaElements[index.index]);
    if (segmentOnly && direction === undefined) {
      this.onSetActiveSegment(mediaType, {index: index.index, segment: segmentOnly});
      return;
    }
    const isActivePlayableMedia = direction === undefined &&
      !this.mediaContainer.currentSegmentId &&
      mediaType === this.mediaContainer.currentMediaType &&
      mediaType.currentMediaIndex === index.index &&
      (mediaType.mediaType === 'Audio' || mediaType.mediaType === 'Video');
    if (isActivePlayableMedia) {
      this.activeMediaComponent?.togglePlayback();
      return;
    }

    this.collapseZoomInButton = true;

    if (this.mediumScreen) {
      mediaType = this.setNextPrev(mediaType, index, direction);
    }

    this.setCurrentMedia(mediaType, index.index).then(() => {
      const currentMediaId = mediaType.mediaElements[index.index].mediaObject.artifact_id;
      this.mediaUpdated.emit(currentMediaId);
      this.onChangeZoomValue(1);
      if (!this.mediaContainer.currentSegmentId) {
        this.activeMediaComponent?.goToStart();
      }

      if (this.mediumScreen && direction === undefined) {
        this.scrollToSelectedElementOnResizeOrClick();
      }
      setTimeout(() => this.collapseZoomInButton = false, 1000);
    });
  }

  onSetActiveSegment(mediaType: ObjectMediaType, event: { index: number, segment: MediaSegment }) {
    const segmentId = event.segment.segmentId;
    const isActiveMedia = mediaType === this.mediaContainer.currentMediaType &&
      mediaType.currentMediaIndex === event.index;
    if (isActiveMedia) {
      const isCurrentSegment = this.mediaContainer.currentSegmentId === segmentId;
      this.mediaContainer.currentSegmentId = segmentId;
      if (isCurrentSegment) {
        this.activeMediaComponent?.toggleSegmentPlayback(segmentId);
      }
      return;
    }
    this.setCurrentMedia(mediaType, event.index, segmentId).then(() => {
      this.mediaUpdated.emit(mediaType.mediaElements[event.index].mediaObject.artifact_id);
      this.onChangeZoomValue(1);
    });
  }

  onToggleChangeMediaOrder(mediaTypeChangeList?: MediaTypeChanges[]) {
    this.showMediaOrder = !this.showMediaOrder;
    if (this.showMediaOrder) {
      document.getElementsByTagName('body')[0].classList.add('selector-open');
      document.getElementsByTagName('html')[0].classList.add('selector-open');
    } else {
      document.getElementsByTagName('body')[0].classList.remove('selector-open');
      document.getElementsByTagName('html')[0].classList.remove('selector-open');
    }

    if (!mediaTypeChangeList) {
      return;
    }
    // Need to sort the mediaElements based on the new sort order for meta items from "change media order" component,
    // also need to set the new order numbers on the existing items
    for (const mediaTypeChanges of mediaTypeChangeList) {
      for (const mediaType of this.mediaContainer.mediaTypes) {
        if (mediaTypeChanges.objectMediaType.mediaType === mediaType.mediaType) {
          mediaType.mediaElements = this.loopMediaItemsSetOrder(mediaType, mediaTypeChanges)
        }
      }
    }
    this.objectRefresh.emit({});
  }

  private loopMediaItemsSetOrder(mediaType: ObjectMediaType, mediaTypeChanges: MediaTypeChanges): Media[] {
    // Virtual segment elements are not in the media item array, and keep their place first
    const elementsWithNewOrder = mediaType.mediaElements.filter(mediaElement => mediaElement.$$segmentOnly);
    for (const mediaItemChanged of mediaTypeChanges.mediaItems) {
      for (const mediaElement of mediaType.mediaElements) {
        if (mediaElement.mediaItem?.$$mediaId === mediaItemChanged.$$mediaId) {
          mediaElement.mediaItem.order_number = mediaItemChanged.order_number;
          elementsWithNewOrder.push(mediaElement);
          break;
        }
      }
    }
    return elementsWithNewOrder;
  }

  onActiveMediaResized(event: TransitionEvent) {
    if (event.propertyName !== 'height' || event.target !== event.currentTarget) {
      return;
    }
    this.activeMediaComponent?.refitActiveMedia();
  }

  private updateActiveMediaContainerSize() {
    if (this.toggleAnnotations) {
      this.activeMediaContainerSize = 'default';
      return;
    }
    const objectType = this.activeMedia?.object_type;
    if (!objectType) {
      return;
    }
    this.activeMediaContainerSize =
      MediaCarouselComponent.ACTIVE_MEDIA_CONTAINER_SIZES[objectType] ?? 'default';
  }

  onChangeZoomValue(zoom: any) {
    this.zoomValue = zoom.value ? zoom.value : zoom;
  }

  setThumbWidth(width: string) {
    this.mediaContainer.thumbWidth = width;
  }

  canAnnotate() {
    return this.mediaContainer.currentMediaType?.canAnnotate;
  }

  get disablePreviousButton(): boolean {
    return this.mediaPosition <= 1;
  }

  get disableNextButton(): boolean {
    return this.mediaPosition >= this.mediaCount;
  }

  get mediaPosition(): number {
    return selectedMediaPosition(this.mediaContainer);
  }

  get mediaCount() {
    return this.mediaContainer?.mediaTypes?.reduce((acc, mediaType) => acc + mediaType.mediaElements.length, 0) || 0;
  }

  private setNextPrev(mediaType: ObjectMediaType, index: any, direction?: string): ObjectMediaType {
    if (index.nextPrevMediaType) {
      const currentMedia = this.mediaContainer.mediaTypes.indexOf(mediaType);
      const nextMedia = this.mediaContainer.mediaTypes[currentMedia + 1];
      const prevMedia = this.mediaContainer.mediaTypes[currentMedia - 1];

      if (direction === 'next' && nextMedia) {
        mediaType = nextMedia;
      }
      if (direction === 'prev' && prevMedia) {
        mediaType = prevMedia;
        index.index = mediaType.mediaElements.length - 1;
      }
    }
    if (direction) {
      const scrollAmount = this.mediumScreenThumbnailWidth * ((direction === 'prev') ? -1 : 1);
      this.mediaElementContainer.nativeElement.scrollLeft += scrollAmount;
    }
    return mediaType;
  }

  private resize() {
    this.onChangeZoomValue(this.zoomValue);
    this.mediumScreen ? this.scrollToSelectedElementOnResizeOrClick() : this.setThumbWidthFromWindowSize();
  }

  private scrollToSelectedElementOnResizeOrClick() {
    const rootNode = this.elRef.nativeElement.getRootNode();
    const el = (rootNode || document).getElementById(
      'thumb-' + this.mediaContainer.currentMediaType.mediaType + '-' +
      this.mediaContainer.currentMediaType.currentMediaIndex);
    const scrollTo = (el.getBoundingClientRect().left + this.mediaElementContainer.nativeElement.scrollLeft) - 85;
    this.mediaElementContainer.nativeElement.scrollTo(scrollTo, 0);
  }

  private setThumbWidthFromWindowSize() {
    this.setSmallThumbSize = window.innerHeight < 900 && !this.mediumScreen;
    if (this.setSmallThumbSize) {
      this.mediaContainer.thumbWidth = 'small';
    } else {
      this.mediaContainer.thumbWidth = this.mediaContainer.thumbWidth ?? 'medium';
    }
  }

  private async setCurrentMedia(mediaType: ObjectMediaType, mediaIndex: number, segmentId: string = null): Promise<void> {
    this.mediaContainer.currentMediaType = mediaType;
    this.mediaContainer.currentSegmentId =
      segmentId ?? this.getSegmentOnly(mediaType.mediaElements[mediaIndex])?.segmentId ?? null;
    mediaType.currentMediaIndex = mediaIndex;
    this.updateActiveMediaContainerSize();
    this.setMediaTypeCanAnnotate();
    if (this.toggleAnnotations) {
      await this.setCurrentAnnotation();
    }
  }

  private getSegmentOnly(mediaElement: Media): MediaSegment | null {
    return mediaElement?.$$segmentOnly ? mediaElement.mediaObject.$$mediaSegments?.[0] ?? null : null;
  }

  private async setCurrentAnnotation(): Promise<void> {
    const mediaType = this.mediaContainer.currentMediaType;
    if (mediaType.mediaType === 'Image' && this.curAnn) {
      const image = new ImageItem();
      image.image_id = mediaType.mediaElements[mediaType.currentMediaIndex].mediaObject.artifact_id;
      await this.annotationService.setCurAnnotation(
        this.curAnn,
        this.sectionsContainer.rootObject,
        image,
        this.sectionsContainer.rootObject,
        null,
        true).then();
      if (this.curAnn.selectedAnnotation) {
        this.curAnn.readOnly = true;
      }
    }
  }

  onOpenImageFullScreen(activeImage: any) {
    this.openImageFullScreen.emit(activeImage);
  }

  onOpenModelFullscreen(activeModel: any) {
    this.openModelFullScreen.emit(activeModel);
  }
}
